import React from "react";
import { userModule, cleanupSDKData } from "../providers/RakutenSDK/RakutenSDK";
import { MESSAGES } from "./constants";
import User from "../dto/User";
import { getWithExpiry, removeWithExpiry, setWithExpiry } from "./cachedData";

interface UserDetails extends User {
  enterprise_id?: string;
  enterprise?: {
    enterprise_id?: string;
    name?: string;
  };
  entity_id?: string;
  count_of_unread_inbox_messages_for_user: number;
  count_of_unread_inbox_messages_for_enterprise: number;
  token_valid_until?: string;
  reseller_id?: string;
}

interface UserState {
  isAuthenticated: boolean;
  user_details: UserDetails;
  token_valid_until?: string;
}

interface Action {
  type: string;
  payload: any;
}

const UserStateContext = React.createContext({} as UserState);
const UserDispatchContext = React.createContext({} as React.Dispatch<Action>);

function userReducer(state: UserState, action: Action): UserState {
  switch (action.type) {
    case MESSAGES.SUCCESS.LOGIN:
      return {
        ...state,
        isAuthenticated: true,
        user_details: action.payload,
      };
    case MESSAGES.SUCCESS.SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
      };
    case MESSAGES.INBOX.UNREAD_COUNT:
      return {
        ...state,
        user_details: {
          ...state.user_details,
          count_of_unread_inbox_messages_for_user:
            action.payload.count_of_unread_inbox_messages_for_user,
        },
      };
    case MESSAGES.DETAILS.TOKEN_VALIDITY:
      return {
        ...state,
        user_details: {
          ...state.user_details,
          token_valid_until: action.payload.token_valid_until
        },
      }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function UserProvider({ children }) {
  const initialState: UserState = {
    //initialize scope here
    isAuthenticated: !!getWithExpiry("user_details"),
    user_details: JSON.parse(getWithExpiry("user_details")) || {
      name: "User",
      title: "",
    },
  };
  const [state, dispatch] = React.useReducer(userReducer, initialState);
  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  return context;
}

function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }
  return context;
}

function signOut(dispatch, { admin_user_status }) {
  removeWithExpiry("user_details");
  cleanupSDKData();
  dispatch({ type: MESSAGES.SUCCESS.SIGN_OUT });

  // to expire token - api call
  userModule.logout(admin_user_status ? "admin" : "enterprise");
}

function setUnreadNotificationCount(dispatch, count = 0) {
  setWithExpiry(
    "user_details",
    JSON.stringify({
      ...JSON.parse(getWithExpiry("user_details")),
      count_of_unread_inbox_messages_for_user: count,
    }),
  );
  dispatch({
    type: MESSAGES.INBOX.UNREAD_COUNT,
    payload: { count_of_unread_inbox_messages_for_user: count },
  });
}

function setTokenValidity(dispatch, token_valid_until){
  setWithExpiry(
    "user_details",
    JSON.stringify({
      ...JSON.parse(getWithExpiry("user_details")),
      token_valid_until
    }),
  );
  dispatch({ type: MESSAGES.DETAILS.TOKEN_VALIDITY, payload: { token_valid_until }})
}

export {
  UserProvider,
  useUserState,
  useUserDispatch,
  signOut,
  setUnreadNotificationCount,
  setTokenValidity
};
